import { partition } from 'lodash/fp';
import {
  ComponentType,
  createElement,
  FC,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { useDragLayer } from 'react-dnd';
import styled from 'styled-components';

import {
  _useRequestedNodeToPan,
  useSetNodeToPan,
} from 'dashboard/components-lib/FlowPlotter/FlowPlotterController';
import NodeWrapper from 'dashboard/components-lib/FlowPlotter/NodeWrapper';
import useDragSurround from 'dashboard/components-lib/FlowPlotter/useDragSurround';
import { usePanZoomBoxControls } from 'dashboard/components-lib/PanZoomBox';
import { isFlowCanvasUseDragLayer } from 'dashboard/dnd/types';
import {
  getIdentityTransformationMatrix2D,
  getTransformationMatrix2DParams,
} from 'dashboard/lib/utils/cssTransformationMatrix';
import useDebounced from 'lib/hooks/useDebounced';

const CANVAS_OUTER_OFFSET = 150;

const Zoomable = styled.div.attrs<{ canvasSize: [number, number] }>(({ canvasSize: [x, y] }) => ({
  style: {
    width: x + CANVAS_OUTER_OFFSET,
    height: y + CANVAS_OUTER_OFFSET,
  },
}))<{ canvasSize: [number, number] }>`
  transform-origin: 0 0;
`;

export const BASE_ELEVATION = 5;

const Nodes = styled.div<{ dragLayer?: boolean; behindEdges?: boolean }>`
  position: absolute;
  width: 100%;
  height: 100%;
  inset: 0;

  z-index: ${({ dragLayer, behindEdges }) =>
    BASE_ELEVATION + (dragLayer ? 1 : behindEdges ? -2 : 0)};
  ${({ dragLayer }) => (dragLayer ? `pointer-events: none;` : '')}
`;

const Edges = styled.svg.attrs(() => ({
  width: '100%',
  height: '100%',
  xmlns: 'http://www.w3.org/2000/svg',
}))`
  & > path {
    transition: 1ms;
  }
  overflow: visible;
  position: absolute;
  z-index: ${BASE_ELEVATION - 1};
`;

export interface BaseEdgeProps {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
}

export interface BaseNodeProps {
  dragged: boolean;
  dragging: boolean;
  draggingInProgress: boolean;
  isUnderDraggedItem: boolean;
  scale: number;
  surrounded: boolean;
  onNodeCustomEvent: (payload: Record<string, unknown> & { key: string }) => void;
}

export enum EdgePosition {
  Top = 'top',
  Bottom = 'bottom',
  Left = 'left',
  Right = 'right',
  Center = 'center',
}

interface Edge {
  componentKey: string;
  componentProps?: Record<string, any>;
  from: Node['id'];
  fromPos?: EdgePosition;
  to: Node['id'];
  toPos?: EdgePosition;
  z?: number;
}

export enum NodeInteractionMode {
  Disabled = 'disabled',
  Drag = 'drag',
  Drop = 'drop',
  Passthrough = 'passthrough',
}
interface Node {
  id: string;
  x: number;
  y: number;
  z?: number;
  componentKey: string;
  componentProps?: Record<string, any>;
  interactionMode?: NodeInteractionMode;
}

interface FlowCanvasProps {
  edges: Edge[];
  edgeComponents: { [componentKey: string]: ComponentType<any> };
  nodes: Node[];
  nodeComponents: { [componentKey: string]: ComponentType<any> };
  onDragAndDrop?: (payload: { draggedNodeId: string; droppedNodeId: string }) => void;
  onNodeCustomEvent?: (
    payload: Parameters<BaseNodeProps['onNodeCustomEvent']>[0] & { nodeId: string },
  ) => void;
  onSurroundNodes?: (surroundedNodeIds: string[]) => void;
}

const getDimensionsFor = (node: HTMLDivElement) => {
  const { width, height, transform } = window.getComputedStyle(node);
  const widthInPx = width.split('px')[0];
  const heightInPx = height.split('px')[0];
  const [_scaleX, _shearY, _shearX, _scaleY, x, y] = transform
    .split(/\(|,|\)/)
    .slice(1, -1)
    .map(parseFloat);

  return { x, y, width: Number(widthInPx), height: Number(heightInPx) };
};

const pointVertexCoordinates = (
  { x, y, width, height }: ReturnType<typeof getDimensionsFor>,
  edgePosition: EdgePosition,
): [number, number] => {
  switch (edgePosition) {
    case EdgePosition.Center:
      return [x + width / 2, y + height / 2];
    case EdgePosition.Bottom:
      return [x + width / 2, y + height];
    case EdgePosition.Top:
      return [x + width / 2, y];
    case EdgePosition.Left:
      return [x, y + height / 2];
    case EdgePosition.Right:
      return [x + width, y + height / 2];
  }
};

const FlowCanvas: FC<FlowCanvasProps> = ({
  edgeComponents,
  edges,
  nodeComponents,
  nodes,
  onDragAndDrop,
  onNodeCustomEvent,
  onSurroundNodes,
}) => {
  const nodeElements = useRef<Record<string, HTMLDivElement>>({});
  const [nodesAbove, nodesBelow] = partition(
    ({ z }) => (z ?? BASE_ELEVATION) >= BASE_ELEVATION,
    nodes,
  );

  // Forcible re-render mechanism is for the situation like:
  // * initial rendering of edges which can't be caught by React's lifecycle because of "ref"
  // * content of node or edge is updated visually (expects it has CSS transition)
  const [_, forceRerender] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    forceRerender();
  }, []);
  const [debouncedForceRerender] = useDebounced(() => {
    forceRerender();
  }, 150);

  const {
    attachUndraggable: disablePanZoomInteraction,
    centerTarget,
    panScreen,
    scrollRef,
    transformationMatrixRef,
    zoomableRef,
  } = usePanZoomBoxControls();

  const collectedProps = useDragLayer<GlobalUseDragLayer>((monitor) => ({
    draggedItem: monitor.getItem(),
    draggedPos: monitor.getDifferenceFromInitialOffset(),
    draggingInProgress: monitor.isDragging(),
  }));

  const { draggedPos, draggingInProgress, draggedItem } = isFlowCanvasUseDragLayer(collectedProps)
    ? collectedProps
    : { draggedPos: null, draggingInProgress: null, draggedItem: null };

  const draggedNode = useMemo(
    () => (draggedItem == null ? null : (nodes.find(({ id }) => id === draggedItem?.id) ?? null)),
    [draggedItem, nodes],
  );

  const { dragSurroundLayer, isSurrounded, surrounding } = useDragSurround({
    enabled: onSurroundNodes != null,
    undraggables: Object.values(nodeElements.current),
    onSurroundEnd: () => {
      const surroundedIds = Object.entries(nodeElements.current)
        .filter(([_, value]) => value && isSurrounded(value.getBoundingClientRect()))
        .map(([id]) => id);

      if (surroundedIds.length > 0) {
        onSurroundNodes?.(surroundedIds);
      }
    },
  });

  const edgeParameters = edges
    .filter((edge) => nodeElements.current[edge.from] && nodeElements.current[edge.to])
    .map((edge) => {
      const [fromX, fromY] = pointVertexCoordinates(
        getDimensionsFor(nodeElements.current[edge.from]),
        edge.fromPos ?? EdgePosition.Bottom,
      );
      const [toX, toY] = pointVertexCoordinates(
        getDimensionsFor(nodeElements.current[edge.to]),
        edge.toPos ?? EdgePosition.Top,
      );

      return [
        { fromX, fromY, toX, toY, ...edge?.componentProps, z: edge?.z ?? BASE_ELEVATION },
        [edge.from, fromX ?? 0, fromY ?? 0, edge.to, toX ?? 0, toY ?? 0].join('<->'),
        edge.componentKey,
      ] as const;
    })
    .filter(([{ fromX, fromY, toX, toY }]) => {
      return [fromX, fromY, toX, toY].every((v) => !Number.isNaN(v));
    })
    .sort((left, right) => (left[0].z > right[0].z ? 1 : -1))
    .map(([props, key, componentKey]) => {
      return {
        props,
        key,
        component: edgeComponents[componentKey],
      };
    });

  const createNode = useCallback(
    (node: Node, props: Omit<BaseNodeProps, 'onNodeCustomEvent'>) => {
      return createElement(nodeComponents[node.componentKey], {
        ...node?.componentProps,
        ...props,
        onNodeCustomEvent: (payload: Parameters<BaseNodeProps['onNodeCustomEvent']>[0]) =>
          onNodeCustomEvent?.({ ...payload, nodeId: node.id }),
      });
    },
    [nodeComponents, onNodeCustomEvent],
  );

  const currentScale = getTransformationMatrix2DParams(
    transformationMatrixRef?.current ?? getIdentityTransformationMatrix2D(),
  ).scaleX;

  const [rightEdge, bottomEdge, negativeHorizontalOffeset]: [number, number, number] =
    useMemo(() => {
      return nodes.reduce(
        ([rightEdge, bottomEdge, negativeHorizontalOffset], node) => [
          rightEdge > node.x ? rightEdge : node.x,
          bottomEdge > node.y ? bottomEdge : node.y,
          negativeHorizontalOffset < node.x ? negativeHorizontalOffset : node.x,
        ],
        [100, 100, 0],
      );
    }, [nodes]);

  useEffect(() => {
    const canvas = zoomableRef.current;
    const rerenderIfTransformed = (e: TransitionEvent) => {
      if (e.propertyName === 'transform') debouncedForceRerender();
    };

    // By hooking starting transition of nodes to hide edges as soon as earlier
    // and re-render at the end to draw accurate edges after its transition ends
    canvas?.addEventListener('transitionstart', rerenderIfTransformed);
    canvas?.addEventListener('transitionend', rerenderIfTransformed);

    return () => {
      canvas?.removeEventListener('transitionstart', rerenderIfTransformed);
      canvas?.removeEventListener('transitionend', rerenderIfTransformed);
    };
  }, [nodes, debouncedForceRerender, zoomableRef]);

  const requestedNodeToPan = _useRequestedNodeToPan();
  const setNodeToPan = useSetNodeToPan();

  useEffect(() => {
    if (requestedNodeToPan != null) {
      setNodeToPan(null);
      centerTarget({ dom: nodeElements.current[requestedNodeToPan], scale: currentScale });
    }
  }, [requestedNodeToPan, nodeElements, currentScale, centerTarget, setNodeToPan]);

  useEffect(() => {
    const horizontalOffsetToCenterOfFirstNode =
      nodes[0].x +
      nodeElements.current[nodes[0].id].clientWidth / 2 -
      (scrollRef.current?.clientWidth ?? 0) / 2;
    panScreen(negativeHorizontalOffeset - horizontalOffsetToCenterOfFirstNode, 0);
  }, []);

  return (
    <>
      {dragSurroundLayer}
      <Zoomable
        canvasSize={[rightEdge + negativeHorizontalOffeset * -1, bottomEdge]}
        ref={surrounding ? null : zoomableRef}
      >
        <Nodes dragLayer>
          {draggingInProgress && draggedNode && draggedPos && (
            <NodeWrapper
              dragged
              nodeId={draggedNode.id}
              x={draggedNode.x + draggedPos.x + negativeHorizontalOffeset * -1}
              y={draggedNode.y + draggedPos.y}
              z={BASE_ELEVATION}
              renderNode={() =>
                createNode(draggedNode, {
                  dragging: true,
                  dragged: false,
                  draggingInProgress: draggingInProgress ?? false,
                  isUnderDraggedItem: false,
                  scale: currentScale,
                  surrounded: false,
                })
              }
            />
          )}
        </Nodes>
        <Nodes>
          {nodesAbove.map((node) => (
            <NodeWrapper
              key={node.id}
              draggable={node.interactionMode === NodeInteractionMode.Drag}
              droppable={node.interactionMode === NodeInteractionMode.Drop}
              nodeId={node.id}
              x={node.x + negativeHorizontalOffeset * -1}
              y={node.y}
              z={node?.z ?? BASE_ELEVATION}
              onDraggingEnd={({ draggedNodeId, droppedNodeId }) => {
                if (onDragAndDrop != null && droppedNodeId != null) {
                  onDragAndDrop({ draggedNodeId, droppedNodeId });
                }
              }}
              ref={(element) => {
                if (element == null) return;
                if (node.interactionMode !== NodeInteractionMode.Passthrough) {
                  disablePanZoomInteraction(element);
                }
                if (element) {
                  nodeElements.current[node.id] = element;
                }
              }}
              renderNode={({ isUnderDraggedItem }) =>
                createNode(node, {
                  dragging: false,
                  dragged: draggedItem?.id === node.id,
                  draggingInProgress: draggingInProgress ?? false,
                  isUnderDraggedItem,
                  scale: currentScale,
                  surrounded:
                    surrounding &&
                    nodeElements.current[node.id] &&
                    isSurrounded(nodeElements.current[node.id].getBoundingClientRect()),
                })
              }
            />
          ))}
        </Nodes>
        <Edges>
          {edgeParameters.map((ep) => (
            <Fragment key={ep.key}>{createElement(ep.component, ep.props)}</Fragment>
          ))}
        </Edges>
        <Nodes behindEdges>
          {/* TODO(kengo): generalize rendering of NodeWrapper to avoid repeating this here */}
          {nodesBelow.map((node) => (
            <NodeWrapper
              key={node.id}
              draggable={node.interactionMode === NodeInteractionMode.Drag}
              droppable={node.interactionMode === NodeInteractionMode.Drop}
              nodeId={node.id}
              x={node.x + negativeHorizontalOffeset * -1}
              y={node.y}
              z={node?.z ?? BASE_ELEVATION}
              onDraggingEnd={({ draggedNodeId, droppedNodeId }) => {
                if (onDragAndDrop != null && droppedNodeId != null) {
                  onDragAndDrop({ draggedNodeId, droppedNodeId });
                }
              }}
              ref={(element) => {
                if (element == null) return;
                if (node.interactionMode !== NodeInteractionMode.Passthrough) {
                  disablePanZoomInteraction(element);
                }
                if (element) {
                  nodeElements.current[node.id] = element;
                }
              }}
              renderNode={({ isUnderDraggedItem }) =>
                createNode(node, {
                  dragging: false,
                  dragged: draggedItem?.id === node.id,
                  draggingInProgress: draggingInProgress ?? false,
                  isUnderDraggedItem,
                  scale: currentScale,
                  surrounded:
                    surrounding &&
                    nodeElements.current[node.id] &&
                    isSurrounded(nodeElements.current[node.id].getBoundingClientRect()),
                })
              }
            />
          ))}
        </Nodes>
      </Zoomable>
    </>
  );
};

export default FlowCanvas;
